import React, { useState, useRef } from 'react';
import FormDataJson from 'form-data-json-convert';
import Warning from '../Warning';
import { checkIncompatibilities, formatIncompatibilityAlert } from '../../../utils/incompatibility';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { mutateImageUrl } from '../../../wordpress';
import { isCreateYourOwnHeadProductId } from '../../../constants';

const ProductVariant = ({
    parentProduct,
    product,
    section,
    isSelected = false,
    onSelect = () => {},
    onCustomise = () => {},
    projectTitle = '',
    boldProductOptions = null,
    selectedProducts = {},
    // currentBuildIndex = 0,
}) => {
    const classes = classNames('product', { 'product-selected': isSelected, 'product-sale': product.on_sale });

    const [compatWarning, setCompatWarning] = useState(null);
    const [pendingAction, setPendingAction] = useState(null);
    const [showReplaceItemWarning, setShowReplaceItemWarning] = useState(false);
    const deferredReplaceActionRef = useRef(null);

    // console.log('ProductVariant: ', { parentProduct, product });

    const prodIdParsed = product && product.id.split('/').pop();
    const imgSrc = mutateImageUrl(product.image && product.image.url, 480, 480);
    const isCreateYourOwnHead = isCreateYourOwnHeadProductId(parentProduct?.id);
    const isCustomisable =
        (boldProductOptions && boldProductOptions[1]?.option_sets?.length > 0) ||
        isCreateYourOwnHead;
    const multiVariant = product.variants?.nodes?.length > 1;
    const hasReverseImage = product?.reverseImage?.reference?.image?.url;

    const handleConfirmCompat = () => {
        if (pendingAction) {
            const fn = pendingAction;
            setPendingAction(null);
            setCompatWarning(null);
            fn();
        }
    };

    const handleDenyCompat = () => {
        setPendingAction(null);
        setCompatWarning(null);
    };

    const performSelection = (productFormJsonValues) => {
        onSelect(parentProduct, '', '', product, productFormJsonValues);
    };

    const existing = selectedProducts?.[section];
    const existingId = existing?.product?.id;
    const currentId = parentProduct?.id;
    const selectVariant = (e) => {
        const productFormJsonValues = FormDataJson.toJson(e.target.form);
        if (section !== 'accessories') {
            if (existingId && existingId !== currentId) {
                deferredReplaceActionRef.current = () => performSelection(productFormJsonValues);
                setShowReplaceItemWarning(true);
                return;
            }
        }

        try {
            const incompat = checkIncompatibilities({
                currentBuild: { selected: selectedProducts },
                candidateProduct: parentProduct,
                candidateSection: section,
            });
            if (incompat) {
                const msg = formatIncompatibilityAlert(incompat);
                if (msg) {
                    setCompatWarning(msg);
                    setPendingAction(() => () => performSelection(productFormJsonValues));
                    return;
                }
            }
        } catch (err) {
            // eslint-disable-next-line no-console
            console.warn('Variant incompatibility check failed', err);
        }
        performSelection(productFormJsonValues);
    };

    const getActionButton = () => {
        if (isCustomisable || hasReverseImage || multiVariant) {
            return (
                <button
                    type="button"
                    className="btn add-to-build"
                    onClick={() => onCustomise(section === 'heads' || section === 'legs' ? parentProduct : product)}
                >
                    <span>Customise</span>
                </button>
            );
        }

        return (
            <button
                type="button"
                className="btn add-to-build"
                onClick={(e) => {
                    selectVariant(e);
                }}
            >
                <span>Add</span>
            </button>
        );
    };

    const computeInStock = (p) => {
        if (typeof p?.totalInventory === 'number') {
            return p.totalInventory > 0;
        }
        const variantNodes = p?.variants?.nodes || (p?.variants?.edges ? p.variants.edges.map((e) => e.node) : []);
        if (variantNodes.length) {
            const summed = variantNodes.reduce(
                (acc, v) => acc + (typeof v?.quantityAvailable === 'number' ? v.quantityAvailable : 0),
                0
            );
            if (summed > 0) return true;
            // As a secondary signal, if any variant is flagged availableForSale
            if (variantNodes.some((v) => v?.availableForSale)) return true;
        }
        // Fall back to product.availableForSale when no inventory counts provided
        if (typeof p?.availableForSale === 'boolean') return p.availableForSale;
        // Default optimistic (will still be gated by add-to-cart logic elsewhere)
        return true;
    };

    const button = existingId && existingId === currentId ? null : getActionButton();

    const isInStock = computeInStock(product);
    const isSoldOut = !product.availableForSale || !isInStock;

    if (isSoldOut) return null;

    return (
        <li className={classes} key={product.id}>
            <button
                type="button"
                className="product-customise product-variant"
                onClick={() => {
                    onCustomise(parentProduct);
                }}
            >
                {imgSrc ? (
                    <img src={imgSrc} width="480" height="480" alt="" loading="lazy" />
                ) : (
                    <span className="image-placeholder" />
                )}
            </button>

            <div className="button-icons">
                {(isCustomisable || hasReverseImage || multiVariant) && (
                    <span>
                        <img
                            src="https://cdn.shopify.com/s/files/1/0223/5446/0736/files/icon-customise.svg?v=1756990022"
                            alt="Customise"
                        />
                    </span>
                )}
            </div>

            <span className="product-title">
                <form
                    action="/cart/add"
                    method="post"
                    encType="multipart/form-data"
                    id={`AddToCartForm-${prodIdParsed}`}
                >
                    <input type="number" name="quantity" value="1" hidden readOnly />
                    <input name="id" value={product.id.split('/').pop()} hidden readOnly />
                    <input
                        type="text"
                        id={`projectTitle-${product.id.split('/').pop()}`}
                        name="properties[projectTitle]"
                        value={projectTitle ? projectTitle : ''}
                        hidden
                        readOnly
                    />
                </form>

                <span className="vh">{parentProduct.title.split('-')[0]}</span>
            </span>

            {compatWarning && (
                <Warning
                    title={compatWarning.title}
                    warningText={`${compatWarning.message}\n\nProceed anyway?`}
                    actionConfirm={handleConfirmCompat}
                    actionDeny={handleDenyCompat}
                />
            )}

            {showReplaceItemWarning && (
                <Warning
                    warningText={'Are you sure you want to make this change?'}
                    actionConfirm={() => {
                        const fn = deferredReplaceActionRef.current;
                        setShowReplaceItemWarning(false);
                        deferredReplaceActionRef.current = null;
                        if (fn) fn();
                    }}
                    actionDeny={() => {
                        setShowReplaceItemWarning(false);
                        deferredReplaceActionRef.current = null;
                    }}
                />
            )}

            {!product.availableForSale ? (
                <p className="btn button span disabled soldout-3">
                    <span>Sold out</span>
                </p>
            ) : (
                button
            )}
        </li>
    );
};

ProductVariant.propTypes = {
    parentProduct: PropTypes.object.isRequired,
    product: PropTypes.object.isRequired,
    section: PropTypes.string.isRequired,
    isSelected: PropTypes.bool,
    onSelect: PropTypes.func,
    onRemove: PropTypes.func,
    onCustomise: PropTypes.func,
    projectTitle: PropTypes.string,
    boldProductOptions: PropTypes.any,
    selectedProducts: PropTypes.object,
};

export default ProductVariant;
